import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, MapPin, Navigation, Phone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export function Location() {
  const [settings, setSettings] = useState<any>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase.from('site_settings').select('*').single();
      if (data) setSettings(data);
    };
    fetchSettings();
  }, []);

  const whatsappLink = `https://wa.me/${settings?.whatsapp_number || '918900992020'}?text=${encodeURIComponent(
    "Hello Walnut Farm Estate, I'm trying to find the property. Could you please share the exact location and directions?"
  )}`;

  return (
    <section id="location" className="py-24 lg:py-40 bg-secondary px-6 overflow-hidden">
      <div className="max-w-screen-2xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 lg:gap-40 items-start">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="space-y-16"
          >
            <div>
              <span className="font-serif !text-[#B8863B] mb-6 block lowercase italic text-xl">
                Find Your Way
              </span>
              <h2 className="heading-luxury !text-[#176B5B] font-bold">
                PRIME <br />
                <span className="italic !text-[#B8863B]">LOCATION.</span>
              </h2>
            </div>

            <div className="space-y-12">
              <div className="flex items-start space-x-8">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-background shrink-0 mt-1">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="space-y-4">
                  <h4 className="font-serif !text-[#176B5B] opacity-60 lowercase italic text-lg">Official Address</h4>
                  <address className="not-italic font-clean text-base md:text-lg leading-relaxed !text-[#59635C]">
                    {settings?.address ? settings.address.split(',').map((part: string, i: number) => (
                      <React.Fragment key={i}>
                        {part.trim()}{i < settings.address.split(',').length - 1 && <br />}
                      </React.Fragment>
                    )) : (
                      <>
                        511, Flora Farms, Pushta Road,<br />
                        Sector 135, Noida, Uttar Pradesh 201304
                      </>
                    )}
                  </address>
                </div>
              </div>

              <div className="flex items-start space-x-8">
                <div className="w-12 h-12 rounded-full border border-primary/10 flex items-center justify-center text-primary shrink-0 mt-1">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="space-y-4">
                  <h4 className="font-serif !text-[#176B5B] opacity-60 lowercase italic text-lg">Contact Support</h4>
                  <p className="font-clean text-base md:text-lg !text-[#59635C]">
                    {settings?.contact_phone || '+91 89009 92020'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 pt-12 border-t border-primary/5">
              <a
                href="https://www.google.com/maps/search/?api=1&query=28.479179,77.382331&query_place_id=ChIJoZilM7LnDTkRQ9LH0fjQ9lI"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center space-x-3 bg-primary text-background px-10 py-5 rounded-full font-clean font-bold uppercase tracking-widest text-sm hover:bg-primary/90 transition-all shadow-xl shadow-primary/10"
              >
                <Navigation className="w-4 h-4" />
                <span>Get Directions</span>
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center space-x-3 border border-primary/20 px-10 py-5 rounded-full font-clean font-bold uppercase tracking-widest text-sm hover:bg-background transition-all"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Enquire via WhatsApp</span>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5 }}
            className="relative w-full aspect-square lg:aspect-[4/5] bg-background shadow-2xl overflow-hidden grayscale contrast-125 hover:grayscale-0 transition-all duration-1000"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14035.3145138745!2d77.382331!3d28.479179!3m2!1i1024!2i1024!2i768!4f13.1!3m3!1m2!1s0x390ce7b233a598c1%3A0x52f6d0f8d1c7d243!2sWalnut%20Farm!5e0!3m2!1sen!2sin!4v1715000000000!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
            
            <div className="absolute inset-0 border-[24px] border-background pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
